import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Configurations/AuthContext";
import { isPlanExpired, calculateTimeLeft } from "./dateUtils";
import DefaultData from "../../CompanyData/DefaultData";

export default function TopHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const [timeLeft, setTimeLeft] = useState("");
  const planIsExpired = user ? isPlanExpired(user.endDate) : false;
  
  // Check if company type is DMC
  const isDMCCompany = user?.companyType?.toLowerCase() === "dmc";

  // LOGIC 1: Determine if the countdown timer should be displayed
  const shouldDisplayCountdown =
    user &&
    !planIsExpired &&
    (user.planType?.toLowerCase() === "trail" || user.isRenewal === false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // LOGIC 2: Automatic redirect hook if the plan is expired
  useEffect(() => {
    if (loading || !user) return;
    if (planIsExpired && location.pathname !== "/subscriptionplans") {
      navigate("/subscriptionplans", { replace: true });
    }
  }, [user, loading, location.pathname, navigate, planIsExpired]);

  // LOGIC 3: The countdown timer hook
  useEffect(() => {
    if (!shouldDisplayCountdown || !user?.endDate) {
      setTimeLeft("");
      return;
    }
    const intervalId = setInterval(() => {
      const timeLeftString = calculateTimeLeft(user.endDate);
      if (timeLeftString === "Plan Expired") {
        clearInterval(intervalId);
        navigate("/subscriptionplans", { replace: true }); // Force redirect when timer hits zero
      } else {
        setTimeLeft(timeLeftString);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [user, navigate, shouldDisplayCountdown]);

  if (loading) return null;

  return (
    <header
      className="shadow-sm"
      style={{
        backgroundColor: DefaultData.THEMECOLOR3,
        height: "70px",
        position: "sticky",
        top: 0,
        zIndex: 1030,
      }}
    >
      <div className="d-flex justify-content-between align-items-center h-100 px-4">
        <div className="d-flex align-items-center">
          <Link to="/dashboard">
            <img
              src="/assets/images/Zotrai.png"
              alt={`${DefaultData.DEFAULTBRANDNAME} Logo`}
              style={{ height: "30px" }}
            />
          </Link>
        </div>
        <div className="d-flex align-items-center">
          {!planIsExpired && (
            <>
              {/* Hide Your Website, Wallet Balance, and Connects for DMC companies */}
              {!isDMCCompany && (
                <>
                  {/* Website Link */}
                  <Link
                    to="/agent-website-showcase"
                    className="btn btn-soft-secondary d-none d-sm-flex align-items-center me-3"
                    style={{ backgroundColor: DefaultData.THEMECOLOR1 }}
                  >
                    <i
                      className="ri-global-line me-2 fs-4"
                      style={{ color: DefaultData.THEMECOLOR3 }}
                    ></i>
                    <div className="d-flex flex-column align-items-start lh-1">
                      <span style={{ color: DefaultData.THEMECOLOR3 }}>
                        Your Website
                      </span>
                      <small
                        style={{
                          color: DefaultData.THEMECOLOR3,
                          paddingTop: "5px",
                        }}
                      >
                        Manage Templates
                      </small>
                    </div>
                  </Link>
                  
                  {/* Wallet Balance */}
                  <Link
                    to="/topuphistory"
                    className="btn btn-soft-secondary d-none d-sm-flex align-items-center me-3"
                    style={{ backgroundColor: DefaultData.THEMECOLOR1 }}
                  >
                    <i
                      className="ri-wallet-3-line me-2 fs-4"
                      style={{ color: DefaultData.THEMECOLOR3 }}
                    ></i>
                    <div className="d-flex flex-column align-items-start lh-1">
                      <span className="" style={{ color: DefaultData.THEMECOLOR3 }}>
                        {user?.currency || ""} {user?.walletBalance || 0}
                      </span>
                      <small
                        style={{
                          color: DefaultData.THEMECOLOR3,
                          paddingTop: "5px",
                        }}
                      >
                        Wallet Balance
                      </small>
                    </div>
                  </Link>
                  
                  {/* Connects */}
                  <Link
                    to="/credithistory"
                    className="btn btn-soft-secondary d-none d-sm-flex align-items-center me-3"
                    style={{ backgroundColor: DefaultData.THEMECOLOR1 }}
                  >
                    <i
                      className="ri-copper-coin-line me-2 fs-4"
                      style={{ color: DefaultData.THEMECOLOR1 }}
                    ></i>
                    <div className="d-flex flex-column align-items-start lh-1">
                      <span className="" style={{ color: DefaultData.THEMECOLOR3 }}>
                        {user?.connects || 0} Connects
                      </span>
                      <small
                        style={{
                          color: DefaultData.THEMECOLOR3,
                          paddingTop: "5px",
                        }}
                      >
                        Recharge
                      </small>
                    </div>
                  </Link>
                </>
              )}
            </>
          )}
          <div className="dropdown">
            <button
              type="button"
              className="btn"
              data-bs-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              style={{
                backgroundColor: DefaultData.THEMECOLOR1,
                color: DefaultData.THEMECOLOR3,
              }}
            >
              <span className="d-flex align-items-center">
                <span className="text-start">
                  <span className="d-none d-xl-inline-block fw-medium user-name-text">
                    Welcome, {user?.brandName}
                  </span>
                  <div className="d-none d-xl-block fs-12 user-name-sub-text lh-sm">
                    {user?.planType && (
                      <span
                        className="badge text-uppercase"
                        style={{
                          backgroundColor: DefaultData.THEMECOLOR3,
                          color: DefaultData.THEMECOLOR4,
                        }}
                      >
                        {user.planType}
                      </span>
                    )}
                    {/* Display the countdown timer or expired message here */}
                    {shouldDisplayCountdown && timeLeft && (
                      <span className="d-block mt-1 text-white">
                        {timeLeft}
                      </span>
                    )}
                    {planIsExpired && (
                      <span className="d-block mt-1 text-danger fw-bold">
                        Plan Expired
                      </span>
                    )}
                  </div>
                </span>
                <i className="ri-arrow-down-s-line ms-2"></i>
              </span>
            </button>
            <div className="dropdown-menu dropdown-menu-end">
              <h6 className="dropdown-header">Welcome!</h6>
              {!planIsExpired && (
                <>
                  <Link className="dropdown-item" to={"/companyprofile"}>
                    <i className="ri-user-line text-muted fs-4 align-middle me-1" />
                    <span className="align-middle">Profile</span>
                  </Link>
                  {/* <Link className="dropdown-item" to={"/gstreport"}>
                    <i className="ri-file-chart-line text-muted fs-4 align-middle me-1" />

                    <span className="align-middle">Reports</span>
                  </Link> */}
                  
                  {/* Hide Corporate link for DMC companies */}
                  {!isDMCCompany && (
                    <Link className="dropdown-item" to={"/team-outing-requests"}>
                      <i className="ri-admin-line text-muted fs-4 align-middle me-1" />
                      <span className="align-middle">Corporate</span>
                    </Link>
                  )}
                 
                  {/* <Link className="dropdown-item" to={"/landingpage"}>
                    <i className="ri-earth-line text-muted fs-4 align-middle me-1" />
                    <span className="align-middle">Website</span>
                  </Link>
                  <Link className="dropdown-item" to={"/suppliers"}>
                    <i className="ri-folder-settings-line text-muted fs-4 align-middle me-1" />
                    <span className="align-middle">Settings</span>
                  </Link>
                  <Link className="dropdown-item" to={"/sasscompanieslist"}>
                    <i className="ri-folder-settings-line text-muted fs-4 align-middle me-1" />
                    <span className="align-middle">Admin Settings</span>
                  </Link>
                  <Link className="dropdown-item" to={"/agents"}>
                    <i className="ri-user-line text-muted fs-4 align-middle me-1" />
                    <span className="align-middle">Agents</span>
                  </Link> */}
                  
                  {/* Hide Agents link for DMC companies */}
                  {!isDMCCompany && (
                    <Link className="dropdown-item" to={"/company/agents"}>
                      <i className="ri-admin-line text-muted fs-4 align-middle me-1" />
                      <span className="align-middle">Agents</span>
                    </Link>
                  )}
                </>
              )}
              <button className="dropdown-item" onClick={handleLogout}>
                <i className="mdi mdi-logout text-muted fs-4 align-middle me-1" />
                <span className="align-middle">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}